import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const AUTH_COOKIE_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

const getCookieValue = (cookieName) => {
    const name = `${cookieName}=`;
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i += 1) {
        const cookie = cookies[i].trim();
        if (cookie.indexOf(name) === 0) {
            const rawValue = cookie.substring(name.length);
            try {
                return decodeURIComponent(rawValue);
            } catch (error) {
                return '';
            }
        }
    }

    return '';
};

export class AuthFetchError extends Error {
    constructor(message, status, data) {
        super(message);
        this.name = 'AuthFetchError';
        this.status = status;
        this.data = data;
    }
}

export const getAuthToken = () => getCookieValue('authToken');

export const getUserIdFromCookie = () => getCookieValue('userId') || null;

export const buildAuthCookie = (name, value, maxAgeSeconds = AUTH_COOKIE_MAX_AGE_SECONDS) => (
    `${name}=${encodeURIComponent(value ?? '')}; path=/; max-age=${maxAgeSeconds}; Secure; SameSite=Strict`
);

export const setAuthCookies = (token, userId) => {
    document.cookie = buildAuthCookie('authToken', token);
    document.cookie = buildAuthCookie('userId', userId);
};

const parseJsonSafely = async (response) => {
    try {
        return await response.json();
    } catch (error) {
        return null;
    }
};

export const clearAuthCookies = () => {
    document.cookie = 'authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Strict';
    document.cookie = 'userId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; SameSite=Strict';
};

export const useFetchWithAuth = (url, options) => {
    const navigate = useNavigate();
    const optionsKey = useMemo(() => JSON.stringify(options ?? {}), [options]);

    const fetchWithAuth = useCallback(async () => {
        const requestOptions = JSON.parse(optionsKey);
        const headers = {
            ...(requestOptions.headers ?? {}),
            Authorization: `Bearer ${getAuthToken()}`,
        };
        const response = await fetch(url, {
            ...requestOptions,
            headers,
        });
        const data = await parseJsonSafely(response);

        if (response.status === 401) {
            navigate('/login');
            return null;
        }

        if (!response.ok) {
            throw new AuthFetchError(data?.message || 'Request failed.', response.status, data);
        }

        return data;
    }, [navigate, optionsKey, url]);

    return fetchWithAuth;
};
